/**
 * アップロード検知 `/dlp/upload`(docs/dlp-upload.md §10)。エージェントが窓ごとに送る推定送信量。
 * FilterBar(種類・帰属・カテゴリ・端末・期間・検索)+ DataTable + CSV(operator)。条件は URL に載せ、CSV にも同じ条件を渡す。
 * 送った中身は分からない(§1)ので、文言は「推定」で統一し、利用者は「割当」と書く。
 */
import { Alert, Button, Segmented, Select, Space } from "antd";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { DlpUploadEvent } from "../../lib/api";
import { jst } from "../../lib/format";
import {
  DataTable,
  FilterBar,
  PageHeader,
  PopClose,
  StatusTag,
  TimeAgo,
  dateRangeCondition,
  useSearchParamInput,
  useSearchParamsPatch,
  type ConditionDef,
  type DataColumn,
} from "../../ui";
import { bytesText, deviceCell, employeeCell, uploadAttributionTag, uploadKindTag } from "./bits";
import { UPLOAD_ATTRIBUTIONS, UPLOAD_CATEGORIES, UPLOAD_KINDS, processDirLabel, uploadCategoryLabel } from "./constants";
import { errorText, useDlpDownload, useDlpPerms, useDlpUploadEvents } from "./hooks";
import { DeviceSelect } from "./selects";
import { UploadHelp } from "./UploadHelp";

export function UploadEventsPage() {
  const { canView, canOperate, canAdmin, hasUpload } = useDlpPerms();
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const [qInput, setQInput] = useSearchParamInput("q");
  const q = sp.get("q") ?? "";
  const kind = sp.get("kind") ?? "";
  const attribution = sp.get("attribution") ?? "";
  const category = sp.get("category") ?? "";
  const deviceId = sp.get("device_id") ?? "";
  const since = sp.get("since") ?? "";
  const until = sp.get("until") ?? "";
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const download = useDlpDownload();

  const filters: Record<string, string> = {
    ...(q ? { q } : {}),
    ...(kind ? { kind } : {}),
    ...(attribution ? { attribution } : {}),
    ...(category ? { category } : {}),
    ...(deviceId ? { device_id: deviceId } : {}),
    ...(since ? { since } : {}),
    ...(until ? { until } : {}),
  };
  // 門はルート(RequireFeature)にもあるが、問い合わせも機能・ロールに合わせる(契約外の API を叩かない)
  const query = useDlpUploadEvents(
    { ...filters, limit: String(pageSize), offset: String((page - 1) * pageSize) },
    hasUpload && canView,
  );
  const filterKey = `${q}|${kind}|${attribution}|${category}|${deviceId}|${since}|${until}`;
  useEffect(() => setPage(1), [filterKey]);
  const rows = query.data?.items ?? [];

  const columns: DataColumn<DlpUploadEvent>[] = [
    {
      title: "時刻",
      ellipsis: false,
      dataIndex: "window_end",
      width: 180,
      render: (v: string, r) => (
        <div>
          <TimeAgo iso={v} />
          <span className="ui-cell-sub ui-num">
            {jst(r.window_start)} 〜
          </span>
        </div>
      ),
    },
    { title: "種類", ellipsis: false, width: 170, render: (_: unknown, r) => uploadKindTag(r.kind, r.attribution) },
    { title: "端末", ellipsis: false, width: 180, render: (_: unknown, r) => deviceCell(r.device) },
    { title: "利用者(割当)", ellipsis: false, width: 200, render: (_: unknown, r) => employeeCell(r.employee) },
    {
      title: "プロセス",
      ellipsis: false,
      width: 320,
      render: (_: unknown, r) => (
        <div>
          <span className="ui-num">{r.process}</span>
          <span className="ui-cell-sub">{processDirLabel(r.process_dir)}</span>
        </div>
      ),
    },
    {
      title: "宛先",
      ellipsis: false,
      width: 320,
      render: (_: unknown, r) =>
        r.kind === "unknown_bulk" ? (
          <div>
            <span style={{ color: "var(--ux-text-tertiary)" }}>宛先不明</span>
            {r.remote_ips.length > 0 && <span className="ui-cell-sub ui-num">{r.remote_ips.join(", ")}</span>}
          </div>
        ) : (
          <div>
            <span className="ui-num">{r.rule_domain}</span>
            {r.category && <span className="ui-cell-sub">{uploadCategoryLabel(r.category)}</span>}
          </div>
        ),
    },
    {
      title: "送信量(推定)",
      ellipsis: false,
      dataIndex: "bytes_sent",
      width: 180,
      render: (v: number, r) => (
        <div>
          <span className="ui-num">{bytesText(v)}</span>
          <span className="ui-cell-sub ui-num">
            {r.flows} 接続 · {r.protocols.join(" / ").toUpperCase()}
          </span>
        </div>
      ),
    },
    { title: "帰属", ellipsis: false, width: 110, render: (_: unknown, r) => uploadAttributionTag(r.attribution) },
    {
      title: "取りこぼし",
      ellipsis: false,
      width: 110,
      render: (_: unknown, r) =>
        r.lost ? (
          <StatusTag tone="warning" dot>
            あり
          </StatusTag>
        ) : (
          ""
        ),
    },
  ];

  const deviceLabel = deviceId
    ? (rows.find((r) => String(r.device?.id) === deviceId)?.device?.hostname ?? `#${deviceId}`)
    : "";
  const conditions: ConditionDef[] = [
    {
      key: "category",
      label: "カテゴリ",
      value: category ? uploadCategoryLabel(category) : "",
      onClear: () => patch({ category: null }, true),
      control: (close) => (
        <>
          <Select
            autoFocus
            style={{ minWidth: 180 }}
            value={category || undefined}
            placeholder="カテゴリ"
            options={UPLOAD_CATEGORIES}
            onChange={(v) => {
              patch({ category: v ?? null }, true);
              close();
            }}
          />
          <PopClose onClick={close} />
        </>
      ),
    },
    {
      key: "device_id",
      label: "端末",
      value: deviceLabel,
      onClear: () => patch({ device_id: null }, true),
      control: (close) => (
        <>
          <DeviceSelect
            value={deviceId ? Number(deviceId) : null}
            onChange={(id) => {
              patch({ device_id: id ? String(id) : null }, true);
              if (id) close();
            }}
            autoFocus
          />
          <PopClose onClick={close} />
        </>
      ),
    },
    dateRangeCondition({
      key: "range",
      label: "期間",
      from: since,
      to: until,
      onChange: (from, to) => patch({ since: from || null, until: to || null }, true),
    }),
  ];
  const filtered = Object.keys(filters).length > 0;
  const clearAll = () => {
    setQInput("");
    patch({ q: null, kind: null, attribution: null, category: null, device_id: null, since: null, until: null }, true);
  };

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="アップロード検知"
          description={
            <>
              一覧に載った送信先への送信を推定検知したもの(窓ごとの送信量)。送った中身・ファイル名は分からず、アップロードの確証ではない。
              利用者は端末の割当。 <UploadHelp />
            </>
          }
          secondary={
            <Space>
              <Link to="/dlp/upload/domains">{canAdmin ? "送信先の一覧を編集" : "送信先の一覧"}</Link>
              {canOperate && (
                <Button onClick={() => void download(`/api/dlp/upload/events.csv?${new URLSearchParams(filters).toString()}`)}>
                  CSV
                </Button>
              )}
            </Space>
          }
        />
        <FilterBar
          search={{ value: qInput, onChange: setQInput, placeholder: "プロセス / 宛先" }}
          views={{
            items: [{ key: "all", label: "すべて" }, ...UPLOAD_KINDS.map((k) => ({ key: k.value, label: k.label }))],
            active: kind || "all",
            onChange: (key) => patch({ kind: key === "all" ? null : key }, true),
          }}
          conditions={conditions}
          right={
            <Space size={8}>
              <Segmented
                size="small"
                aria-label="帰属"
                value={attribution || "all"}
                options={[{ value: "all", label: "帰属: すべて" }, ...UPLOAD_ATTRIBUTIONS]}
                onChange={(v) => patch({ attribution: v === "all" ? null : String(v) }, true)}
              />
              {filtered && (
                <Button size="small" onClick={clearAll}>
                  条件を外す
                </Button>
              )}
            </Space>
          }
        />
        {query.isError && (
          <Alert type="error" showIcon message="アップロード検知を取得できなかった" description={errorText(query.error)} />
        )}
        <DataTable<DlpUploadEvent>
          columns={columns}
          scrollX={columns.reduce((sum, c) => sum + c.width, 0)}
          rows={rows}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={query.data?.total ?? 0}
          page={page}
          pageSize={pageSize}
          onPage={(p, s) => {
            setPage(p);
            setPageSize(s);
          }}
          rowTone={(row) => (row.kind === "unknown_bulk" ? "warning" : undefined)}
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{
            text: filtered
              ? "条件に合う検知はない"
              : "検知はまだない。設定の「観測する」が有効なら、閾値を超えた送信が端末の次の report で届く",
            action: filtered ? <Button onClick={clearAll}>条件を外す</Button> : undefined,
          }}
        />
      </div>
    </div>
  );
}
